import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';

import Landing from './pages/Landing';
import HostLogin from './pages/auth/HostLogin';
import AdminLogin from './pages/auth/AdminLogin';

import HostLayout from './layouts/HostLayout';
import AdminLayout from './layouts/AdminLayout';

import HostDashboard from './pages/host/Dashboard';
import HostProperties from './pages/host/Properties';
import HostPropertyDetail from './pages/host/PropertyDetail';
import HostPropertyForm from './pages/host/PropertyForm';
import HostPropertyImages from './pages/host/PropertyImages';
import HostBookings from './pages/host/Bookings';
import HostBookingDetail from './pages/host/BookingDetail';
import HostCustomers from './pages/host/Customers';
import HostMessages from './pages/host/Messages';
import HostReviews from './pages/host/Reviews';
import HostPayments from './pages/host/Payments';
import HostPayoutSetup from './pages/host/PayoutSetup';
import HostSupport from './pages/host/Support';
import HostProfile from './pages/host/Profile';
import HostSubscriptionRequired from './pages/host/SubscriptionRequired';

import AdminDashboard from './pages/admin/Dashboard';
import AdminHosts from './pages/admin/Hosts';
import AdminUsers from './pages/admin/AdminUsers';
import AdminProperties from './pages/admin/Properties';
import AdminBookings from './pages/admin/Bookings';
import AdminCustomers from './pages/admin/Customers';
import AdminReviews from './pages/admin/Reviews';
import AdminTestimonials from './pages/admin/Testimonials';
import AdminMasterData from './pages/admin/MasterData';
import AdminFinance from './pages/admin/Finance';
import AdminAuditLogs from './pages/admin/AuditLogs';
import AdminTickets from './pages/admin/SupportTickets';
import AdminMessages from './pages/admin/Messages';
import AdminSettings from './pages/admin/Settings';

const SessionLoader = () => (
  <div className="min-h-screen grid place-items-center bg-slate-50">
    <div className="flex items-center gap-2 text-sm text-slate-500">
      <Loader2 className="h-4 w-4 animate-spin text-teal-700" /> Loading console…
    </div>
  </div>
);

function Protected({ kind, children, allowInactiveHost = false }) {
  const { user, kind: sessionKind, loading } = useAuth();
  if (loading) return <SessionLoader />;
  if (!user || sessionKind !== kind) return <Navigate to={`/${kind}/login`} replace />;
  if (
    kind === 'host' &&
    !allowInactiveHost &&
    user?.subscription &&
    user.subscription.active === false
  ) {
    return <Navigate to="/host/subscription" replace />;
  }
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/host/login" element={<HostLogin />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/host/subscription" element={<Protected kind="host" allowInactiveHost><HostSubscriptionRequired /></Protected>} />

            <Route path="/host" element={<Protected kind="host"><HostLayout /></Protected>}>
              <Route index element={<Navigate to="/host/dashboard" replace />} />
              <Route path="dashboard" element={<HostDashboard />} />
              <Route path="properties" element={<HostProperties />} />
              <Route path="properties/new" element={<HostPropertyForm />} />
              <Route path="properties/:id" element={<HostPropertyDetail />} />
              <Route path="properties/:id/edit" element={<HostPropertyForm />} />
              <Route path="properties/:id/images" element={<HostPropertyImages />} />
              <Route path="bookings" element={<HostBookings />} />
              <Route path="bookings/:id" element={<HostBookingDetail />} />
              <Route path="customers" element={<HostCustomers />} />
              <Route path="messages" element={<HostMessages />} />
              <Route path="reviews" element={<HostReviews />} />
              <Route path="payments" element={<HostPayments />} />
              <Route path="payout-setup" element={<HostPayoutSetup />} />
              <Route path="support" element={<HostSupport />} />
              <Route path="profile" element={<HostProfile />} />
            </Route>

            <Route path="/admin" element={<Protected kind="admin"><AdminLayout /></Protected>}>
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="hosts" element={<AdminHosts />} />
              <Route path="admin-users" element={<AdminUsers />} />
              <Route path="properties" element={<AdminProperties />} />
              <Route path="bookings" element={<AdminBookings />} />
              <Route path="customers" element={<AdminCustomers />} />
              <Route path="messages" element={<AdminMessages />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="testimonials" element={<AdminTestimonials />} />
              <Route path="master-data" element={<AdminMasterData />} />
              <Route path="finance" element={<AdminFinance />} />
              <Route path="audit-logs" element={<AdminAuditLogs />} />
              <Route path="tickets" element={<AdminTickets />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
